import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/landing-mobile.css';

const LandingPage = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      title: '🎯 AI Tutor (24/7 Academic Support)',
      description: 'Your child gets instant help anytime they\'re stuck. The AI Tutor adapts to their strengths and weaknesses, giving personalized explanations until they understand the topic.',
      benefit: 'No more waiting for help. No more falling behind.'
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
      ),
      title: '📚 Past Examination Papers',
      description: 'Access to real past exam papers and structured practice tests.',
      benefit: 'Your child practices with real exam-style questions — so nothing feels unfamiliar on exam day.'
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <line x1="9" y1="9" x2="15" y2="9"/>
          <line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
      ),
      title: '📝 Online Practice Exams',
      description: 'Timed practice exams that simulate the real testing environment.',
      benefit: 'Builds speed, accuracy, and exam confidence.'
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11l3 3L22 4"/>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
        </svg>
      ),
      title: '📊 Weekly Progress Tracking',
      description: 'See exactly how your child is improving.',
      benefit: 'You stay informed and confident that real progress is happening.'
    }
  ];

  const pricingCards = [
    {
      level: 'Grade 7',
      price: 'K500',
      duration: 'Full Year Access',
      description: 'A full year of structured preparation costs less than repeating a grade.'
    },
    {
      level: 'Grade 12',
      price: 'K1000',
      duration: 'Full Year Access',
      description: 'A full year of structured preparation costs less than repeating a grade.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Help Your Child Pass Their Exams With Confidence — Or Your Money Back</h1>
            <p>
              If you're worried your child might struggle in their Grade 7 or Grade 12 exams, you're not alone.
              Zed Scholar is an exam preparation platform designed to turn weak grades into strong results — with structured learning, exam practice, and AI-powered support available 24/7.
            </p>
            <div className="hero-buttons">
              {user ? (
                <Link to="/dashboard" className="btn btn-primary btn-large">
                  Go to Dashboard
                </Link>
              ) : (
                <a 
                  href="https://wa.me/260962306371" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-extra-large"
                >
                  Chat With Us on WhatsApp Now
                </a>
              )}
            </div>
            <div className="limited-spots">
              <strong>The first 100 students will get access to our comprehensive video lessons</strong>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Focus Section */}
      <section className="visual-focus">
        <div className="container">
          <div className="focus-content">
            <div className="focus-text">
              <h2>Imagine your child walking into the exam room fully prepared.</h2>
              <p>No panic. No guessing. No last-minute cramming.</p>
              <div className="benefits-list">
                <div className="benefit-item">
                  <span className="check">✓</span>
                  <span>They recognize question formats.</span>
                </div>
                <div className="benefit-item">
                  <span className="check">✓</span>
                  <span>They've practiced similar papers.</span>
                </div>
                <div className="benefit-item">
                  <span className="check">✓</span>
                  <span>They know exactly how to structure their answers.</span>
                </div>
                <div className="benefit-item">
                  <span className="check">✓</span>
                  <span>They feel calm and confident.</span>
                </div>
              </div>
              <p className="emphasis">That's what proper preparation looks like.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2>Features & Benefits</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="feature-benefit">
                  <strong>Benefit:</strong> {feature.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose">
        <div className="container">
          <h2>Why Parents Choose Zed Scholar</h2>
          <div className="reasons-grid">
            <div className="reason-item">
              <h3>Because private tutors are expensive.</h3>
              <p>Get the same quality of support at a fraction of the cost.</p>
            </div>
            <div className="reason-item">
              <h3>Because fear of poor exam results is stressful.</h3>
              <p>Give your child the confidence and preparation they need to succeed.</p>
            </div>
            <div className="reason-item">
              <h3>Because their child deserves the best possible chance to succeed.</h3>
              <p>Comprehensive preparation that covers all bases.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing">
        <div className="container">
          <h2>Invest in Your Child's Future</h2>
          <div className="pricing-cards">
            {pricingCards.map((plan, index) => (
              <div key={index} className="pricing-card">
                <h3>{plan.level}</h3>
                <div className="price">
                  <span className="amount">{plan.price}</span>
                  <span className="duration">{plan.duration}</span>
                </div>
                <p className="pricing-description">{plan.description}</p>
                <a 
                  href="https://wa.me/260962306371" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-full"
                >
                  Talk to Us on WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee">
        <div className="container">
          <div className="guarantee-content">
            <div className="guarantee-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h2>100% Exam Success Guarantee</h2>
            <p>
              We believe in Zed Scholar so strongly that we remove all the risk for you.
            </p>
            <p>
              If your child uses the platform and does not improve and pass their exams,
              <strong>we will refund 100% of your money.</strong>
            </p>
            <div className="guarantee-features">
              <div className="guarantee-feature">
                <span className="checkmark">✓</span>
                <span>No complicated conditions</span>
              </div>
              <div className="guarantee-feature">
                <span className="checkmark">✓</span>
                <span>No stress</span>
              </div>
              <div className="guarantee-feature">
                <span className="checkmark">✓</span>
                <span>No risk</span>
              </div>
            </div>
            <p className="guarantee-closing">Your child's success is our priority.</p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Give Your Child the Preparation They Deserve</h2>
          <p>The first <strong>100 students</strong> will get access to our comprehensive video lessons.</p>
          <p>Don't wait until exam season panic starts.</p>
          <a 
            href="https://wa.me/260962306371" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-primary btn-large"
          >
            Talk to us on WhatsApp and secure one of 100 available spots today
          </a>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
